// 세션 룰 "언제 재생성할지" 판단을 캡슐화.
// - 인메모리 상태를 보유(누적 메시지·생성 횟수·마지막 생성 시각).
// - onMessage()에 입력을 흘리면 정책이 **true/false만** 반환(재생성 시점인지).
// - 정책을 교체해 "첫 메시지 즉시 → 이후 100", "처음 100 → 이후 1000", "시간 간격" 등 자유롭게.
// - Date.now를 주입 가능하게 해서 순수 로직으로 테스트 용이.
//
// 사용:
//   const t = new SessionRuleTrigger(immediateFirstThen(100));
//   if (t.onMessage()) generate();   // true면 재생성 시점
//
// 정책 = (state) => boolean.  state: { generations, sinceLast, total, lastAt, now }
//   generations : 지금까지 fire한 횟수
//   sinceLast   : 마지막 fire 이후 메시지 수
//   total       : 총 메시지 수
//   lastAt      : 마지막 fire 시각(ms)
//   now         : 현재 시각(ms)

// 첫 메시지에 즉시 1회, 이후 step개마다. (현재 기본 동작)
export function immediateFirstThen(step = 100) {
  return (s) => (s.generations === 0 ? s.total >= 1 : s.sinceLast >= step);
}

// generation 회차별 임계. 예) steps=[100], repeat=1000 → 처음 100, 이후 1000마다.
// (즉시-첫생성 없음 — 첫 생성도 임계를 채워야 함)
export function stepCount(steps = [100], repeat = null) {
  return (s) => {
    const need =
      s.generations < steps.length ? steps[s.generations] : repeat != null ? repeat : steps[steps.length - 1];
    return s.sinceLast >= need;
  };
}

// 시간 간격: 마지막 생성 후 everyMs 지나고 최소 minMessages개 쌓였으면.
export function timeInterval({ everyMs = 60000, minMessages = 1 } = {}) {
  return (s) => s.sinceLast >= minMessages && s.now - s.lastAt >= everyMs;
}

// 콜드→스테디: 첫 생성(gen0)은 first개로 빠르게, 이후는 steadyCount개 AND steadyMs 경과.
// 캐시로 워밍 스타트했으면 markGenerated()로 gen1로 씨딩 → 빠른-first 건너뛰고 바로 스테디.
export function coldThenSteady({ first = 100, steadyCount = 1000, steadyMs = 600000 } = {}) {
  return (s) =>
    s.generations === 0 ? s.sinceLast >= first : s.sinceLast >= steadyCount && s.now - s.lastAt >= steadyMs;
}

export class SessionRuleTrigger {
  constructor(policy = immediateFirstThen(100)) {
    this.policy = policy;
    this.reset();
  }

  reset() {
    this._s = { generations: 0, sinceLast: 0, total: 0, lastAt: 0 };
  }

  get state() {
    return { ...this._s };
  }

  // 외부에서 "방금 생성한 셈"으로 상태 전진(캐시 워밍 스타트 등). 메시지 없이 호출.
  markGenerated({ now = Date.now() } = {}) {
    this._s.generations += 1;
    this._s.sinceLast = 0;
    this._s.lastAt = now;
  }

  // 메시지 1건 도착. now 주입 가능(시간 정책 테스트용). fire면 내부 상태 전진 후 true 반환.
  onMessage({ now = Date.now() } = {}) {
    this._s.sinceLast += 1;
    this._s.total += 1;
    const fire = !!this.policy({ ...this._s, now });
    if (fire) {
      this._s.generations += 1;
      this._s.sinceLast = 0;
      this._s.lastAt = now;
    }
    return fire;
  }
}
